import { Spinner } from "@blueprintjs/core";
import * as Lucide from "lucide-react";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import IntroductionAudioButton from "@/components/pieces/roleplays/IntroductionAudioButton";
import {
  CallStatus,
  LiveCallRenderProps,
} from "@/components/pieces/roleplays/LiveCallManager";
import { MicrophoneCheck } from "@/components/pieces/roleplays/MicrophoneCheck";
import { formatTime } from "@/components/pieces/roleplays/utils";
import { Button } from "@/components/ui/button";
import { Presentation } from "@/types/roleplays";

import { PresentationsPicker } from "./PresentationsPicker";

type ScenarioCallControlsProps =
  | {
      liveCallRenderProps: LiveCallRenderProps;
      callStatus: CallStatus.IDLE | CallStatus.STARTING;
      scenarioId: string;

      /** This is for visual testing on Storybook */
      playIntroductionAudioOnRender?: boolean;
    }
  | {
      liveCallRenderProps: LiveCallRenderProps;
      callStatus:
        | CallStatus.CONNECTING
        | CallStatus.CONNECTED
        | CallStatus.ENDING
        | CallStatus.ERROR
        | CallStatus.RESTARTING
        | CallStatus.SAVE_FAILED;
      presentations: Presentation[];
      selectedPresentation: Presentation | null;
      onPresentationChange: (presentation: Presentation | null) => void;
      enableScreenSharing: boolean;
      startScreenSharing: () => Promise<void>;
      stopScreenSharing: () => void;
      isScreenSharing: boolean;
      scenarioId: string;
    };

export function ScenarioCallControls(props: ScenarioCallControlsProps) {
  const { liveCallRenderProps } = props;
  switch (props.callStatus) {
    case CallStatus.IDLE:
      return (
        <div>
          {liveCallRenderProps.introductionAudioUrl ? (
            <IntroductionAudioButton
              initialIsPlaying={props.playIntroductionAudioOnRender}
              audioUrl={liveCallRenderProps.introductionAudioUrl}
              onFinished={liveCallRenderProps.onStartCall}
            />
          ) : (
            <>
              <Button
                className="w-[200px]"
                intent="primary"
                variant="solid"
                size="lg"
                onClick={liveCallRenderProps.onStartCall}
              >
                <Lucide.Phone
                  fill="white"
                  strokeWidth={0}
                  size={16}
                  color="white"
                />
                Begin Session
              </Button>
            </>
          )}
        </div>
      );
    case CallStatus.STARTING:
      return (
        <div className="grid grid-flow-col gap-2">
          <Button className="w-[200px]" intent="primary" disabled size="lg">
            Starting in {liveCallRenderProps.callState.countDown}
            ...
          </Button>
          <Tooltip content="Cancel" placement="top">
            <Button
              intent="secondary"
              size="lg"
              variant="solid"
              onClick={liveCallRenderProps.onCancelCall}
            >
              <Lucide.X size={16} />
            </Button>
          </Tooltip>
        </div>
      );
    case CallStatus.CONNECTING:
      return (
        <div className="flex flex-col gap-2 items-center">
          <Button className="w-[200px]" intent="primary" size="lg" disabled>
            <Spinner size={16} color="white" />
            Connecting...
          </Button>

          <div className="min-h-5 sm:h-5">
            <MicrophoneCheck />
          </div>
        </div>
      );
    case CallStatus.CONNECTED: {
      const {
        presentations,
        onPresentationChange,
        scenarioId,
        selectedPresentation,
        enableScreenSharing,
        startScreenSharing,
        stopScreenSharing,
        isScreenSharing,
      } = props;
      const canUsePresentations =
        presentations.length > 0 && onPresentationChange;
      return (
        <div className="grid grid-cols-[repeat(2,auto)] sm:grid-cols-[1fr_auto_1fr] gap-2 items-center">
          {(canUsePresentations || enableScreenSharing) && (
            <div className="justify-self-end">
              <PresentationsPicker
                scenarioId={scenarioId}
                selectedPresentation={selectedPresentation}
                presentations={presentations}
                onPresentationChange={onPresentationChange}
                enableScreenSharing={enableScreenSharing}
                startScreenShare={startScreenSharing}
                stopScreenShare={stopScreenSharing}
                isScreenSharing={isScreenSharing}
              />
            </div>
          )}
          <Button
            className={
              enableScreenSharing || canUsePresentations
                ? "justify-self-start"
                : "justify-self-end"
            }
            intent="success"
            variant="solid"
            size="lg"
            onClick={liveCallRenderProps.onEndCall}
          >
            <div className="grid w-27 justify-center grid-flow-col gap-2 items-center">
              <Lucide.PhoneOff strokeWidth={1} fill="white" size={16} />
              End
            </div>
          </Button>
          <div className="grid items-center w-full grid-cols-[auto_auto_auto] sm:grid-cols-[auto_auto_1fr] justify-center gap-2 justify-self-end sm:justify-self-start row-2 sm:row-1 col-[1/span_2] sm:col-3">
            <Tooltip content="Restart" placement="top">
              <Button
                size="lg"
                onClick={liveCallRenderProps.onRestartCall}
                intent="secondary"
              >
                <Lucide.RotateCcw size={16} />
              </Button>
            </Tooltip>
            <Tooltip content="Cancel" placement="top">
              <Button
                size="lg"
                onClick={liveCallRenderProps.onCancelCall}
                intent="secondary"
              >
                <Lucide.X size={16} />
              </Button>
            </Tooltip>
            <div className="mr-4 justify-self-end text-end">
              {formatTime(liveCallRenderProps.callDuration)}
            </div>
          </div>
        </div>
      );
    }

    case CallStatus.RESTARTING:
      return (
        <div className="grid grid-flow-col gap-2 mb-7 justify-center">
          <Button className="w-[200px]" intent="primary" disabled size="lg">
            Restarting in {liveCallRenderProps.callState.countDown}
            ...
          </Button>
          <Tooltip content="Cancel" placement="top">
            <Button
              size="lg"
              onClick={liveCallRenderProps.onCancelCall}
              intent="secondary"
            >
              <Lucide.X size={16} />
            </Button>
          </Tooltip>
        </div>
      );
    case CallStatus.ENDING:
      return (
        <div className="grid grid-flow-col gap-4 items-center justify-center">
          <Button intent="success" disabled variant="solid" size="lg">
            <div className="grid w-27 grid-flow-col gap-2 items-center">
              <Spinner size={16} color="white" />
              Saving...
            </div>
          </Button>
          <div className="w-20 text-start">
            {formatTime(liveCallRenderProps.callDuration)}
          </div>
        </div>
      );
    case CallStatus.ERROR:
      return (
        <Button
          className="justify-self-center"
          intent="danger"
          variant="outline"
          size="lg"
          onClick={liveCallRenderProps.onRestartCall}
        >
          <Lucide.RotateCcw size={16} />
          Try Again
        </Button>
      );
    case CallStatus.SAVE_FAILED:
      return (
        <Button intent="primary" className="justify-self-center" size="lg">
          <Lucide.RotateCcw size={16} />
          Retry Saving
        </Button>
      );
  }
}
